import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { copyText } from "@/lib/scroll";
import { SectionLabel, Reveal } from "./Reveal";
import { LeadForm } from "./LeadForm";

const Requisites = () => {
  const { t } = useLang();
  const r = t.req;
  const [copied, setCopied] = useState(null);
  const doCopy = async (key, text) => {
    if (await copyText(text)) {
      setCopied(key);
      toast.success(r.copied);
      setTimeout(() => setCopied(null), 1600);
    }
  };
  const all = r.rows.map((x) => `${x.k}: ${x.v}`).join("\n");
  return (
    <div id="requisites" className="rounded-[28px] bg-white/60 border border-[#0A0D14]/10 p-6 md:p-8 h-full" data-testid="requisites-block">
      <div className="flex items-center justify-between gap-4">
        <h3 className="font-display text-2xl text-[#0A0D14]">{r.title}</h3>
        <button data-testid="copy-requisites-button" onClick={() => doCopy("all", all)} className="text-xs font-semibold inline-flex items-center gap-2 rounded-full bg-[#0A0D14] text-[#F2EBDD] px-4 py-2 hover:bg-[var(--gold-deep)] transition-colors">
          {copied === "all" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} <span className="hidden sm:inline">{r.copyAll}</span>
        </button>
      </div>
      <dl className="mt-6">
        {r.rows.map((x, i) => (
          <div key={i} className="group grid grid-cols-[108px_1fr_auto] sm:grid-cols-[140px_1fr_auto] gap-3 items-start py-3.5 border-b border-[#0A0D14]/10 last:border-0">
            <dt className="text-[11px] sm:text-xs uppercase tracking-[0.04em] text-[#5b5346] pt-0.5 break-words">{x.k}</dt>
            <dd className="text-sm text-[#0A0D14] font-semibold break-words min-w-0" data-testid={`requisite-value-${i}`}>{x.v}</dd>
            <button aria-label={r.copy} data-testid={`copy-requisite-${i}`} onClick={() => doCopy(i, x.v)} className="text-[#5b5346] hover:text-[#0A0D14] transition-colors">
              {copied === i ? <Check className="w-4 h-4 text-[var(--gold-deep)]" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        ))}
      </dl>
    </div>
  );
};

export const Contacts = () => {
  const { t } = useLang();
  return (
    <section id="contacts" className="relative py-28 md:py-36 bg-[#F2EBDD] text-[#0A0D14]" data-testid="contacts-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <SectionLabel n="08" light>{t.contacts.label}</SectionLabel>
        <Reveal>
          <h2 className="font-display text-3xl md:text-6xl mt-8 max-w-3xl">{t.contacts.title}</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-base md:text-lg text-[#3d372e] max-w-2xl">{t.contacts.sub}</p>
        </Reveal>
        <div className="mt-14 grid lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-7">
            <div className="rounded-[28px] bg-[#0A0D14] p-6 md:p-10 h-full">
              <LeadForm />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <Requisites />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
